/**
 * Music from somewhere else in the building.
 *
 * Not a soundtrack. The premise is that a record is playing in a room the
 * player has not found and probably never will, and that they are hearing it
 * through however many walls are in the way. What it must not be is ambiguous:
 * a player who spends a minute deciding whether there is music has been given a
 * puzzle instead of a room, so the layer sits a fixed and measured distance
 * under the ambience bed — plainly there, plainly not for them. When it stops,
 * the silence afterwards should be worse than the silence before, which is why
 * the gaps are minutes long and the fade-out takes its time.
 *
 * It is also the layer with the least business being audible during an
 * encounter, so it is not allowed anywhere near one. The scheduler will not
 * start a piece while the tension bed is up or an entity is nearby, and a piece
 * that is already going ducks and fades if that changes, rather than cutting —
 * a music cue snapping off is a tell, and this layer must never tell the player
 * anything.
 *
 *   piece ─> tape chain ─> body ─> duck ─> out ─┬─> graph.dry
 *   local track ────────────────┘               └─> send ─> graph.reverbIn
 *
 * Where the parts live: `pieces.ts` holds the pieces as data plus the one
 * player that renders any of them, `tape.ts` holds the wow, crackle and wall
 * filter that do most of the actual work, `localmusic.ts` holds the optional
 * path for the player's own files. This file is the scheduling and the mix.
 *
 * Timing is entirely on the audio clock. `update` never accumulates `dt` into
 * a deadline; every decision compares context time against a context time, so
 * a stalled frame or a level-generation hitch cannot make a piece drift, and a
 * suspended context simply freezes the whole layer where it stands. Every time
 * that reaches an `AudioParam` from here is checked first: one non-finite value
 * throws out of `cancelScheduledValues` and silently takes the layer with it,
 * and no music at all is a far worse failure than a mistimed fade.
 */

import { clamp01, damp, smoothstep } from '../core/math';
import { hashSeed, makeRNG } from '../core/rng';
import type { AmbienceProfile, GameContext, RNG } from '../core/types';
import type { AudioGraph } from './graph';
import { LocalTrack, LocalTracks } from './localmusic';
import { PIECES, PiecePlayer, type PieceSpec } from './pieces';
import { makeCrackleBuffer, TapeChain } from './tape';

/**
 * Level of the whole layer, set by measurement rather than by ear.
 *
 * Every piece is rendered alone through an `OfflineAudioContext` and trimmed by
 * its `gain` to the same gated loudness, so this one number moves the set as a
 * unit. It puts the layer about 4.5 dB under the Level 0 ambience bed measured
 * the same way — under the hum, and under it by less than the width of the
 * hum's own slow wander, which is the point. Anything quieter and a player has
 * to decide whether they are imagining it.
 */
const LEVEL = 0.16;
/**
 * The player's own tracks are a deliberate choice, so they get a little more —
 * but they are also full-range mastered audio rather than something pushed
 * through a wall, so the same number would be far louder. Two thirds of the
 * layer level lands them roughly where the pieces sit.
 */
const LOCAL_LEVEL = LEVEL * 0.62;

/**
 * Per-room trim, so "under the bed" stays true on a level whose bed is quiet.
 *
 * Derived from the same offline renders: each bed's gated loudness against
 * Level 0's, halved and floored at −4 dB. Halved because the music is coming
 * through a wall from somewhere else in the building and does not actually know
 * which room the player is standing in — it should lean toward the quieter room
 * without tracking it, and a poolroom is not a reason to lose the layer.
 */
const ROOM_TRIM: Record<AmbienceProfile, number> = {
  lobby: 1,
  warehouse: 0.87,
  pipes: 0.8,
  poolrooms: 0.63,
  menu: 0.8,
  silence: 0.63,
};

/** Seconds. Nothing in the opening minutes; the run has to feel empty first. */
const FIRST_GAP: readonly [number, number] = [105, 230];
/** Seconds between the *starts* of two pieces, which is what a player feels. */
const GAP: readonly [number, number] = [180, 400];
/** Seconds of silence enforced after a piece, including one cut short. */
const MIN_SILENCE = 135;
/** Seconds to wait before re-testing the gates when a start was blocked. */
const RETRY = 25;

const FADE_IN = 9;
const FADE_OUT = 7;
/** Faster exits for the two ways tension can end a piece. */
const FADE_TENSION = 4.5;
const FADE_PANIC = 1.6;
/** A descent. Short, because the screen has already gone black by then. */
const FADE_DESCEND = 2.5;

/**
 * Above this, no piece starts. Tightened along with the layer level: a piece
 * that used to sit half-noticed under the start of a stalk is plainly audible
 * there now, so it has to start later and leave earlier.
 */
const START_TENSION = 0.1;
/** Above this, a playing piece leaves. */
const ABORT_TENSION = 0.2;
/** Metres. An entity this close counts as an encounter whatever the bed says. */
const ENCOUNTER_RANGE = 40;

/** Seconds of material kept ahead of the audio clock. */
const LOOKAHEAD = 2;

/** How long the player's own tracks are allowed to run before fading. */
const LOCAL_MAX_SECONDS = 210;

/**
 * Offsets in cells, sampled around the player to guess how open the space is.
 * Two rings: the near one separates a corridor from a room, the far one
 * separates a room from an atrium.
 */
const OPEN_PROBE: ReadonlyArray<readonly [number, number]> = [
  [2, 0],
  [-2, 0],
  [0, 2],
  [0, -2],
  [2, 2],
  [-2, 2],
  [2, -2],
  [-2, -2],
  [5, 0],
  [-5, 0],
  [0, 5],
  [0, -5],
  [4, 4],
  [-4, 4],
  [4, -4],
  [-4, -4],
];

type State = 'idle' | 'playing' | 'fading';

export class Music {
  private readonly out: GainNode;
  private readonly duck: GainNode;
  private readonly body: GainNode;
  private readonly send: GainNode;
  private readonly local = new LocalTracks();

  private rng: RNG | null = null;
  private state: State = 'idle';
  private player: PiecePlayer | null = null;
  private tape: TapeChain | null = null;
  private track: LocalTrack | null = null;
  private crackleBuffer: AudioBuffer | null = null;

  /** Context times. Everything the scheduler decides is one of these. */
  private nextAt = Infinity;
  private startedAt = 0;
  private endsAt = 0;
  private fadeDoneAt = 0;
  private silentUntil = 0;

  private lastId = '';
  private target = 0;
  private vocal = 1;
  private vocalRecover = 1;
  private appliedDuck = 1;
  private openness = 0;
  private probeIn = 0;
  private levelIndex = -1;
  private roomTrim = ROOM_TRIM.lobby;

  constructor(private readonly g: AudioGraph) {
    const ctx = g.ctx;

    this.out = ctx.createGain();
    this.out.gain.value = 1;
    this.out.connect(g.dry);

    this.send = ctx.createGain();
    this.send.gain.value = 0.7;
    this.out.connect(this.send);
    this.send.connect(g.reverbIn);

    this.duck = ctx.createGain();
    this.duck.gain.value = 1;
    this.duck.connect(this.out);

    this.body = ctx.createGain();
    this.body.gain.value = 0;
    this.body.connect(this.duck);

    // Started here rather than when the first piece is due: the answer takes a
    // round trip and the first piece is minutes away, so it is always in by
    // the time anyone asks.
    this.local.probe();
  }

  /** True while a piece is audible. Exposed for debugging; nothing reads it. */
  get playing(): boolean {
    return this.state !== 'idle';
  }

  /**
   * Pull the music down under something louder — an entity vocalisation, a
   * death. Recovers over `seconds`. Repeated calls take the deepest duck.
   */
  dip(amount: number, seconds: number): void {
    const a = clamp01(amount);
    if (a < this.vocal) {
      this.vocal = a;
      this.vocalRecover = Math.max(0.2, seconds);
    }
  }

  /**
   * Which room the player is in. Only used to trim the layer, and only applied
   * to the next piece — a record does not get quieter halfway through because
   * somebody walked into a tiled corridor.
   */
  setAmbience(profile: AmbienceProfile): void {
    this.roomTrim = ROOM_TRIM[profile] ?? ROOM_TRIM.lobby;
  }

  update(dt: number, ctx: GameContext, tension: number): void {
    // A suspended context has a frozen clock, so every deadline below would be
    // met the instant it resumes. Sit it out instead.
    if (this.g.ctx.state !== 'running') return;
    const now = this.g.now;
    if (!Number.isFinite(now)) return;
    if (!this.rng) this.seed(ctx, now);

    this.updateDuck(dt, now, tension);

    // A record does not follow the player down a hole, and the convolver is
    // being swapped out from under it anyway — a chord held across that swap
    // smears.
    if (ctx.levelIndex !== this.levelIndex) {
      const first = this.levelIndex < 0;
      this.levelIndex = ctx.levelIndex;
      if (!first) this.fade(now, FADE_DESCEND);
    }

    switch (this.state) {
      case 'idle':
        if (now >= this.nextAt) {
          if (this.canStart(ctx, tension, now)) this.start(now);
          else this.nextAt = now + RETRY;
        }
        break;

      case 'playing': {
        // Tension is the priority: a piece is never allowed to be playing over
        // an encounter, whatever it was in the middle of.
        if (tension > ABORT_TENSION || ctx.dead) {
          this.fade(now, tension > 0.5 || ctx.dead ? FADE_PANIC : FADE_TENSION);
        } else if (now >= this.endsAt || (this.track !== null && this.track.ended)) {
          this.fade(now, FADE_OUT);
        }
        break;
      }

      case 'fading':
        if (now >= this.fadeDoneAt) this.stop(now);
        break;
    }

    if (this.state !== 'idle') {
      this.sampleOpenness(dt, ctx);
      this.player?.schedule(now, now + LOOKAHEAD);
      this.player?.sweep(now);
      this.tape?.update(now);
    }
  }

  dispose(): void {
    this.stop(this.g.now);
    this.body.disconnect();
    this.duck.disconnect();
    this.send.disconnect();
    this.out.disconnect();
  }

  /* ---------------------------------------------------------------- *
   * Scheduling
   * ---------------------------------------------------------------- */

  /**
   * Seeded from the level the run started on, so a given seed hears the same
   * pieces at the same times. Deliberately not re-seeded on a descent: the
   * stream has to keep running or every level would open with the same choice.
   */
  private seed(ctx: GameContext, now: number): void {
    this.rng = makeRNG(hashSeed('music') ^ ctx.level.seed);
    this.nextAt = now + this.rng.range(FIRST_GAP[0], FIRST_GAP[1]);
  }

  private canStart(ctx: GameContext, tension: number, now: number): boolean {
    if (ctx.paused || ctx.dead) return false;
    if (now < this.silentUntil) return false;
    if (tension > START_TENSION) return false;
    // The bed lags what is actually happening, so check the world as well:
    // something that has just walked into the room has not moved tension yet.
    for (const e of ctx.entities) {
      if (e.mode === 'dormant' || e.mode === 'dead') continue;
      if (e.distToPlayer < ENCOUNTER_RANGE) return false;
    }
    return true;
  }

  private start(at: number): void {
    const now = this.clock(at);
    const rng = this.rng!;
    const url = this.local.pick(rng);
    if (url) this.startLocal(now, url);
    else this.startPiece(now, this.pickPiece(rng));
  }

  /** Never the same piece twice running; one reroll is enough with eight. */
  private pickPiece(rng: RNG): PieceSpec {
    let spec = rng.pick(PIECES);
    if (spec.id === this.lastId) spec = rng.pick(PIECES);
    this.lastId = spec.id;
    return spec;
  }

  private startPiece(now: number, spec: PieceSpec): void {
    const rng = this.rng!;
    if (!this.crackleBuffer) this.crackleBuffer = makeCrackleBuffer(this.g.ctx, this.g.rng.fork(7));

    this.tape = new TapeChain(this.g, spec.tape, this.crackleBuffer, rng.fork(1), this.body);
    this.tape.setOpenness(this.openness);
    // A quarter second of headroom so the first chord is scheduled ahead of the
    // clock rather than landing on it.
    this.player = new PiecePlayer(this.g, spec, rng, this.tape, now + 0.25);

    this.target = LEVEL * this.roomTrim * spec.gain;
    const length = rng.range(spec.minSeconds, spec.maxSeconds);
    this.endsAt = now + length;

    if (spec.abandon && rng.chance(spec.abandon)) {
      // Whatever is playing it stops partway through and does not start again.
      // The chain is left up for a few seconds afterwards, so the surface noise
      // and the room carry on with nothing on them — a record ending is a
      // sound, and a record being interrupted is the absence of one.
      const cut = now + rng.range(0.35, 0.7) * length;
      this.player.stopAfter(cut);
      this.endsAt = cut + rng.range(5, 12);
    }

    this.player.stopAfter(this.endsAt);
    this.begin(now, spec.tape.send);
  }

  private startLocal(now: number, url: string): void {
    const track = new LocalTrack(this.g.ctx, url, this.body);
    if (!track.out) {
      // No source node — fall back rather than sit through a silent gap.
      track.dispose();
      this.startPiece(now, this.pickPiece(this.rng!));
      return;
    }
    track.start();
    this.track = track;
    this.target = LOCAL_LEVEL * this.roomTrim;
    this.endsAt = now + LOCAL_MAX_SECONDS;
    this.begin(now, 0.28);
  }

  private begin(now: number, send: number): void {
    this.state = 'playing';
    this.startedAt = now;
    ramp(this.body.gain, this.target, now, FADE_IN);
    ramp(this.send.gain, send, now, 1.5);
  }

  private fade(at: number, seconds: number): void {
    if (this.state !== 'playing') return;
    const now = this.clock(at);
    this.state = 'fading';
    this.fadeDoneAt = now + seconds;
    ramp(this.body.gain, 0, now, seconds);
    // Stop placing new material; whatever is already ringing fades out with it.
    this.player?.stopAfter(now);
  }

  /** Tear the piece down and pick a time for the next one. */
  private stop(at: number): void {
    const now = this.clock(at);
    // Order matters: notes hold params on the tape chain's warble bus and hand
    // them back on release, so the player has to go first.
    this.player?.dispose();
    this.player = null;
    this.tape?.dispose();
    this.tape = null;
    this.track?.dispose();
    this.track = null;

    this.body.gain.cancelScheduledValues(now);
    this.body.gain.setValueAtTime(0, now);

    if (this.state !== 'idle') {
      // Measured from where the last piece began, not from where it ended: a
      // piece cut short after twenty seconds should not buy the player an
      // extra-long silence on top of the fright they just had.
      this.silentUntil = now + MIN_SILENCE;
      const rng = this.rng;
      const gap = rng ? rng.range(GAP[0], GAP[1]) : GAP[0];
      this.nextAt = Math.max(this.startedAt + gap, this.silentUntil);
    }
    this.state = 'idle';
  }

  /* ---------------------------------------------------------------- *
   * Mix
   * ---------------------------------------------------------------- */

  private updateDuck(dt: number, now: number, tension: number): void {
    if (this.state === 'idle' && this.vocal >= 1) return;
    this.vocal = Math.min(1, this.vocal + dt / this.vocalRecover);
    // Tension ducks before it aborts, so the music is already on its way out
    // by the time the scheduler decides to end it. Deeper than it was: at the
    // new layer level, 85% down still leaves something plainly audible over the
    // start of an encounter.
    const fromTension = 1 - smoothstep(0.03, ABORT_TENSION, tension) * 0.93;
    const target = Math.min(fromTension, this.vocal);
    if (Math.abs(target - this.appliedDuck) < 0.02) return;
    this.appliedDuck = target;
    this.duck.gain.setTargetAtTime(target, now, 0.22);
  }

  /**
   * A cheap read of how much room is around the player, used to open the wall
   * filter a little in an atrium. Sampling sixteen cells twice a second is
   * nothing next to actually tracing the space, and the filter move is slow
   * enough that a rough answer is all it needs.
   */
  private sampleOpenness(dt: number, ctx: GameContext): void {
    this.probeIn -= dt;
    if (this.probeIn > 0) return;
    this.probeIn = 0.5;

    const grid = ctx.level.grid;
    const c = grid.worldToCell(ctx.player.pos.x, ctx.player.pos.z);
    let open = 0;
    for (const [dx, dy] of OPEN_PROBE) {
      if (!grid.isSolid(c.cx + dx, c.cy + dy)) open++;
    }
    this.openness = damp(this.openness, open / OPEN_PROBE.length, 0.6, 0.5);
    this.tape?.setOpenness(this.openness);
  }

  /**
   * A usable context time. `update` already refuses a non-finite clock, so this
   * only ever catches a caller that reached one of the private transitions with
   * a bad time of its own; falling back to the real clock schedules the fade in
   * slightly the wrong place, which is survivable, where letting it through is
   * not — one `NaN` into `cancelScheduledValues` throws and the layer never
   * makes another sound for the rest of the run.
   */
  private clock(t: number): number {
    return Number.isFinite(t) ? t : this.g.now;
  }
}

function ramp(param: AudioParam, target: number, t: number, seconds: number): void {
  if (!Number.isFinite(t) || !Number.isFinite(target)) return;
  const span = Number.isFinite(seconds) ? Math.max(0.05, seconds) : 0.05;
  param.cancelScheduledValues(t);
  param.setValueAtTime(param.value, t);
  param.linearRampToValueAtTime(target, t + span);
}
